import { useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { INDUSTRIES, Industry, FocusArea } from '../services/cvGenerator';

interface FilterSidepanelProps {
  selectedIndustries: Industry[];
  selectedFocusAreas: FocusArea[];
  availableFocusAreas: FocusArea[];
  selectedTechnologies: string[];
  availableTechnologies: string[];
  filterByDate: boolean;
  startDate: string;
  onToggleIndustry: (industry: Industry) => void;
  onToggleFocusArea: (focusArea: FocusArea) => void;
  onToggleTechnology: (technology: string) => void;
  onSetFilterByDate: (value: boolean) => void;
  onSetStartDate: (value: string) => void;
}

const FilterSidepanel = ({
  selectedIndustries,
  selectedFocusAreas,
  availableFocusAreas,
  selectedTechnologies,
  availableTechnologies,
  filterByDate,
  startDate,
  onToggleIndustry,
  onToggleFocusArea,
  onToggleTechnology,
  onSetFilterByDate,
  onSetStartDate,
}: FilterSidepanelProps) => {
  const { t, language } = useLanguage();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState({
    industries: true,
    focusAreas: true,
    technologies: true,
  });

  const toggleFilter = (filter: keyof typeof expandedFilters): void => {
    setExpandedFilters((prev) => ({
      ...prev,
      [filter]: !prev[filter],
    }));
  };

  const allIndustriesSelected =
    INDUSTRIES.length > 0
    && INDUSTRIES.every((industry) => selectedIndustries.includes(industry));
  const allFocusAreasSelected =
    availableFocusAreas.length > 0
    && availableFocusAreas.every((focusArea) =>
      selectedFocusAreas.includes(focusArea)
    );
  const allTechnologiesSelected =
    availableTechnologies.length > 0
    && availableTechnologies.every((technology) =>
      selectedTechnologies.includes(technology)
    );

  const toggleAllIndustries = (): void => {
    if (allIndustriesSelected) {
      INDUSTRIES.forEach((industry) => {
        if (selectedIndustries.includes(industry)) {
          onToggleIndustry(industry);
        }
      });
      return;
    }

    INDUSTRIES.forEach((industry) => {
      if (!selectedIndustries.includes(industry)) {
        onToggleIndustry(industry);
      }
    });
  };

  const toggleAllFocusAreas = (): void => {
    if (allFocusAreasSelected) {
      availableFocusAreas.forEach((focusArea) => {
        if (selectedFocusAreas.includes(focusArea)) {
          onToggleFocusArea(focusArea);
        }
      });
      return;
    }

    availableFocusAreas.forEach((focusArea) => {
      if (!selectedFocusAreas.includes(focusArea)) {
        onToggleFocusArea(focusArea);
      }
    });
  };

  const toggleAllTechnologies = (): void => {
    if (allTechnologiesSelected) {
      availableTechnologies.forEach((technology) => {
        if (selectedTechnologies.includes(technology)) {
          onToggleTechnology(technology);
        }
      });
      return;
    }

    availableTechnologies.forEach((technology) => {
      if (!selectedTechnologies.includes(technology)) {
        onToggleTechnology(technology);
      }
    });
  };

  if (!isPanelOpen) {
    return (
      <div className="filter-sidepanel collapsed">
        <button
          className="toggle-panel-button"
          onClick={() => setIsPanelOpen(true)}
          title="Open filters"
        >
          ⚙️
        </button>
      </div>
    );
  }

  return (
    <aside className="filter-sidepanel">
      <div className="sidepanel-header">
        <h3>{t.cv.filters || 'Filters'}</h3>
        <button
          className="toggle-panel-button"
          onClick={() => setIsPanelOpen(false)}
          title="Close filters"
        >
          ✕
        </button>
      </div>

      <div className="filters-container">
        {/* Date Filter */}
        <div className="filter-group date-filter-simple">
          <div className="filter-checkboxes">
            <div className="date-input-wrapper">
              <label htmlFor="startDate">{t.cv.fromDate}</label>
              <input
                key={`startDate-${language}`}
                id="startDate"
                type="date"
                lang={language === 'nl' ? 'nl-NL' : 'en-US'}
                value={startDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(event) => onSetStartDate(event.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Industries Filter */}
        <fieldset className="filter-group">
          <legend
            className="filter-toggle"
            onClick={() => toggleFilter('industries')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleFilter('industries');
              }
            }}
          >
            <span className="filter-toggle-arrow">
              {expandedFilters.industries ? '▼' : '▶'}
            </span>
            {t.cv.industries}
          </legend>
          {expandedFilters.industries && (
            <div className="filter-checkboxes">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={allIndustriesSelected}
                  onChange={toggleAllIndustries}
                />
                <span>All</span>
              </label>
              {INDUSTRIES.map((industry) => (
                <label key={industry} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={selectedIndustries.includes(industry)}
                    onChange={() => onToggleIndustry(industry)}
                  />
                  <span>{industry}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>

        {/* Focus Areas Filter */}
        <fieldset className="filter-group">
          <legend
            className="filter-toggle"
            onClick={() => toggleFilter('focusAreas')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleFilter('focusAreas');
              }
            }}
          >
            <span className="filter-toggle-arrow">
              {expandedFilters.focusAreas ? '▼' : '▶'}
            </span>
            {t.cv.focusAreas}
          </legend>
          {expandedFilters.focusAreas && (
            <div className="filter-checkboxes">
              <label className="checkbox-item">
                <input
                  type="checkbox"
                  checked={allFocusAreasSelected}
                  onChange={toggleAllFocusAreas}
                />
                <span>All</span>
              </label>
              {availableFocusAreas.map((focusArea) => (
                <label key={focusArea} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={selectedFocusAreas.includes(focusArea)}
                    onChange={() => onToggleFocusArea(focusArea)}
                  />
                  <span>{focusArea}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>

        {/* Technologies Filter */}
        {availableTechnologies.length > 0 && (
          <fieldset className="filter-group">
            <legend
              className="filter-toggle"
              onClick={() => toggleFilter('technologies')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleFilter('technologies');
                }
              }}
            >
              <span className="filter-toggle-arrow">
                {expandedFilters.technologies ? '▼' : '▶'}
              </span>
              {t.cv.technologies || 'Technologies'}
            </legend>
            {expandedFilters.technologies && (
              <div className="filter-checkboxes">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={allTechnologiesSelected}
                    onChange={toggleAllTechnologies}
                  />
                  <span>All</span>
                </label>
                {availableTechnologies.map((tech) => (
                  <label key={tech} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={selectedTechnologies.includes(tech)}
                      onChange={() => onToggleTechnology(tech)}
                    />
                    <span>{tech}</span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>
        )}
      </div>
    </aside>
  );
};

export default FilterSidepanel;
